/**
 * Reducer for navigation/workflow/UI state in UnifiedParsingModal.
 *
 * Phase 3 refactor: consolidates scattered useState calls into a single
 * source of truth. Sub-hooks (useParsingFlow, useAIAnalysisFlow,
 * useUploadFlow, useDomainSelection, useDataLoaders) keep their own
 * internal state — this reducer covers ONLY the facade-level state
 * that drives navigation, parser settings, selection, and results UI.
 *
 * Excluded from this reducer (kept as dedicated useState):
 *  - `domains`        — passed as ref-style prop into many sub-hooks
 *  - `selectedPhones` — Set<string>, mutates frequently
 */

import type { InputMethod } from '../types'
import { STEP_INDEX } from '../constants'
import type { WorkflowStateResponse } from '@/services/api'

export type ResultsTab = 'domains' | 'pages' | 'urls' | 'phones'

export interface WorkflowState {
  // * Navigation
  /** Current step index (0..7, see STEP_INDEX) */
  currentStep: number
  /** Selected input method, or null when user hasn't chosen yet */
  inputMethod: InputMethod | null

  // * Lifecycle
  /** True after initializeModal has finished (success or fail) */
  isInitialized: boolean
  /** True while initializeModal is fetching server-driven recovery info */
  isRestoring: boolean
  /** True when recovery banner ("Восстановлен AI-анализ") has been shown */
  recoveryBannerShown: boolean
  /** Latest workflow state fetched from backend (if any) */
  serverWorkflow: WorkflowStateResponse | null

  // * Manual / input auxiliary
  manualUrlsText: string
  manualSubsource: string
  processingManualUrls: boolean

  // * Parser settings
  downloadAll: boolean
  minClicks: number
  topQueries: number

  // * Selection (project + queries + regions)
  selectedCrmId: number | null
  selectedQueries: string[]
  selectedRegionCodes: string[]

  // * Results UI
  resultsTab: ResultsTab
  showAllDomains: boolean
}

export const initialWorkflowState: WorkflowState = {
  currentStep: STEP_INDEX.method,
  inputMethod: null,

  isInitialized: false,
  isRestoring: false,
  recoveryBannerShown: false,
  serverWorkflow: null,

  manualUrlsText: '',
  manualSubsource: 'yandex_parsing',
  processingManualUrls: false,

  downloadAll: false,
  minClicks: 5,
  topQueries: 100,

  selectedCrmId: null,
  selectedQueries: [],
  selectedRegionCodes: [],

  resultsTab: 'domains',
  showAllDomains: false,
}

export type WorkflowAction =
  // Lifecycle / recovery
  | { type: 'RESTORE_BEGIN' }
  | { type: 'RESTORE_SUCCESS'; payload: WorkflowStateResponse | null }
  | { type: 'RESTORE_FAIL' }
  | { type: 'INIT_DONE' }
  | { type: 'MARK_RECOVERY_SHOWN' }
  // Navigation
  | { type: 'SET_STEP'; step: number }
  | { type: 'SET_INPUT_METHOD'; method: InputMethod | null }
  // Manual / input
  | { type: 'SET_MANUAL_URLS_TEXT'; value: string }
  | { type: 'SET_MANUAL_SUBSOURCE'; value: string }
  | { type: 'SET_PROCESSING_MANUAL_URLS'; value: boolean }
  // Parser settings
  | { type: 'SET_DOWNLOAD_ALL'; value: boolean }
  | { type: 'SET_MIN_CLICKS'; value: number }
  | { type: 'SET_TOP_QUERIES'; value: number }
  // Selection
  | { type: 'SET_SELECTED_CRM_ID'; value: number | null }
  | { type: 'SET_SELECTED_QUERIES'; value: string[] }
  | { type: 'SET_SELECTED_REGION_CODES'; value: string[] }
  // Results UI
  | { type: 'SET_RESULTS_TAB'; value: ResultsTab }
  | { type: 'SET_SHOW_ALL_DOMAINS'; value: boolean }
  // Bulk reset
  | { type: 'RESET' }
  | { type: 'RESET_KEEP_PROJECT'; projectCrmId: number | null }

export function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case 'RESTORE_BEGIN':
      return { ...state, isRestoring: true }

    case 'RESTORE_SUCCESS':
      return { ...state, isRestoring: false, serverWorkflow: action.payload }

    case 'RESTORE_FAIL':
      return { ...state, isRestoring: false, serverWorkflow: null }

    case 'INIT_DONE':
      return { ...state, isInitialized: true, isRestoring: false }

    case 'MARK_RECOVERY_SHOWN':
      if (state.recoveryBannerShown) return state
      return { ...state, recoveryBannerShown: true }

    case 'SET_STEP':
      if (action.step < 0 || action.step > STEP_INDEX.upload_results) return state
      if (state.currentStep === action.step) return state
      return { ...state, currentStep: action.step }

    case 'SET_INPUT_METHOD':
      if (state.inputMethod === action.method) return state
      return { ...state, inputMethod: action.method }

    case 'SET_MANUAL_URLS_TEXT':
      if (state.manualUrlsText === action.value) return state
      return { ...state, manualUrlsText: action.value }

    case 'SET_MANUAL_SUBSOURCE':
      if (state.manualSubsource === action.value) return state
      return { ...state, manualSubsource: action.value }

    case 'SET_PROCESSING_MANUAL_URLS':
      if (state.processingManualUrls === action.value) return state
      return { ...state, processingManualUrls: action.value }

    case 'SET_DOWNLOAD_ALL':
      if (state.downloadAll === action.value) return state
      return { ...state, downloadAll: action.value }

    case 'SET_MIN_CLICKS':
      if (state.minClicks === action.value) return state
      return { ...state, minClicks: action.value }

    case 'SET_TOP_QUERIES':
      if (state.topQueries === action.value) return state
      return { ...state, topQueries: action.value }

    case 'SET_SELECTED_CRM_ID':
      if (state.selectedCrmId === action.value) return state
      return { ...state, selectedCrmId: action.value }

    case 'SET_SELECTED_QUERIES':
      return { ...state, selectedQueries: action.value }

    case 'SET_SELECTED_REGION_CODES':
      return { ...state, selectedRegionCodes: action.value }

    case 'SET_RESULTS_TAB':
      if (state.resultsTab === action.value) return state
      return { ...state, resultsTab: action.value }

    case 'SET_SHOW_ALL_DOMAINS':
      if (state.showAllDomains === action.value) return state
      return { ...state, showAllDomains: action.value }

    case 'RESET':
      return { ...initialWorkflowState }

    case 'RESET_KEEP_PROJECT':
      return { ...initialWorkflowState, selectedCrmId: action.projectCrmId }

    default:
      return state
  }
}

